/**
 * PostHog Provider Implementation
 * 
 * Simple, clean PostHog integration following official documentation.
 */

import posthog from 'posthog-js';
import BaseProvider from './baseProvider';

class PostHogProvider extends BaseProvider {
  constructor() {
    super();
    this.isInitialized = false;
  }

  init(config) {
    const { apiKey, apiHost } = config;
    
    if (!apiKey) {
      console.error('❌ PostHog: apiKey is required');
      return;
    }

    // Store reference to parent service for callback
    const parentService = window.analyticsService;

    try {
      // Initialize PostHog with recommended configuration
      // Enhanced for PWA compatibility - ensures events work in standalone mode
      posthog.init(apiKey, {
        api_host: apiHost || 'https://app.posthog.com',
        // Disable automatic pageview capture since we're tracking manually
        capture_pageview: false,
        // Disable autocapture for cleaner event tracking
        autocapture: false,
        // Enable persistence for offline support (important for PWAs)
        // PostHog will automatically queue events when offline
        persistence: 'localStorage+cookie',
        // Secure cookies for HTTPS PWAs
        secure_cookie: window.location.protocol === 'https:',
        // Load PostHog asynchronously
        loaded: (posthogInstance) => {
          this.isInitialized = true;
          
          // Mark parent service as initialized
          if (parentService) {
            parentService.isInitialized = true;
          }
          
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ PostHog initialized successfully');
            const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
            console.log('📱 PWA Mode:', isPWA);
          }
        }
      });
    } catch (error) {
      console.error('❌ PostHog initialization error:', error);
      this.isInitialized = false;
    }
  }

  identify(userId, properties = {}) {
    if (!this.isInitialized) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ PostHog: identify called before initialization, queuing...');
      }
      // PostHog will queue events automatically, but we should wait if possible
      // For now, we'll still call it as PostHog handles queuing
    }
    
    try {
      const result = posthog.identify(userId, properties);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ PostHog: Identified user', userId, properties);
        if (result !== undefined) {
          console.log('📊 PostHog identify returned:', result);
        }
      }
      return result;
    } catch (error) {
      console.error('❌ PostHog identify error:', error);
      return null;
    }
  }

  track(eventName, properties = {}) {
    if (!this.isInitialized) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ PostHog: track called before initialization, queuing...');
      }
      // PostHog will queue events automatically, but we should wait if possible
    }
    
    try {
      const result = posthog.capture(eventName, properties);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ PostHog: Tracked event', eventName, properties);
        if (result !== undefined) {
          console.log('📊 PostHog capture returned:', result);
        }
      }
      return result;
    } catch (error) {
      console.error('❌ PostHog track error:', error);
      return null;
    }
  }

  page(pageName, properties = {}) {
    if (!this.isInitialized) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ PostHog: page called before initialization, queuing...');
      }
    }
    
    try {
      const result = posthog.capture('$pageview', {
        page: pageName,
        ...properties
      });
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ PostHog: Tracked pageview', pageName, properties);
        if (result !== undefined) {
          console.log('📊 PostHog capture returned:', result);
        }
      }
      return result;
    } catch (error) {
      console.error('❌ PostHog page error:', error);
      return null;
    }
  }

  reset() {
    if (!this.isInitialized) {
      return;
    }
    
    try {
      posthog.reset();
    } catch (error) {
      console.error('❌ PostHog reset error:', error);
    }
  }

  setUserProperties(properties) {
    if (!this.isInitialized) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ PostHog: setUserProperties called before initialization');
      }
      return;
    }
    
    try {
      posthog.people.set(properties);
    } catch (error) {
      console.error('❌ PostHog setUserProperties error:', error);
    }
  }

  group(groupId, traits = {}) {
    if (!this.isInitialized) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ PostHog: group called before initialization');
      }
      return;
    }
    
    try {
      posthog.groupIdentify(groupId, traits);
    } catch (error) {
      console.error('❌ PostHog group error:', error);
    }
  }

  alias(newId, originalId) {
    if (!this.isInitialized) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ PostHog: alias called before initialization');
      }
      return;
    }
    
    try {
      posthog.alias(newId, originalId);
    } catch (error) {
      console.error('❌ PostHog alias error:', error);
    }
  }

  flush() {
    if (!this.isInitialized) {
      return;
    }
    
    try {
      if (typeof posthog.flush === 'function') {
        posthog.flush();
      }
    } catch (error) {
      console.error('❌ PostHog flush error:', error);
    }
  }

  isFeatureEnabled(flagName) {
    if (!this.isInitialized) {
      return false;
    }
    
    try {
      return posthog.isFeatureEnabled(flagName) || false;
    } catch (error) {
      console.error('❌ PostHog isFeatureEnabled error:', error);
      return false;
    }
  }

  getFeatureFlag(flagName) {
    if (!this.isInitialized) {
      return null;
    }
    
    try {
      return posthog.getFeatureFlag(flagName) || null;
    } catch (error) {
      console.error('❌ PostHog getFeatureFlag error:', error);
      return null;
    }
  }
}

export default PostHogProvider;
